/**
 * The stick, written down.
 *
 * A tape's whole payload is the player's input trace, and at 60 Hz that is the
 * one part of a recording that could plausibly get big — 15 minutes is 54,000
 * ticks. It does not get big, for a reason that is structural rather than
 * lucky: **a rendered frame simulates every one of its ticks with the same
 * `FlightInputs`.** `sim-runner.ts`'s `runTicks` reads the stick once per frame
 * and applies it to all `steps` ticks, so the tick stream is already piecewise
 * constant before any compression is attempted. Run-length encoding it is not a
 * squeeze applied afterwards — it is the shape the data already has.
 *
 * A run is a fixed-order tuple rather than an object:
 *
 * ```
 * [ticks, pitch, roll, yaw, throttle, flags]
 * ```
 *
 * all six integers, axes in `AXIS_SCALE` units and `flags` a three-bit field.
 * That is the same encoding the duel wire uses for a single tick
 * (`net/protocol.ts`'s `EncodedInputs`) with a repeat count on the front, and it
 * costs about 25 characters per *change of stick* rather than per tick.
 */

import { clamp } from "../flight/units.ts";
import { AXIS_SCALE, type FlightInputs } from "../flight/inputs.ts";

const FIRING_BIT = 1;
const CLEAR_JAM_BIT = 2;
/** Bit 4 on the duel wire (`net/protocol.ts`); the same bit here keeps the two codecs aligned. */
const BOMB_RELEASE_BIT = 4;

/** `[ticks, pitch, roll, yaw, throttle, flags]` — all integers. */
export type InputRun = readonly [number, number, number, number, number, number];

/**
 * One axis as an integer. The `+ 0` is not decoration: `Math.round` of a small
 * negative deflection yields **negative zero**, and `JSON.stringify(-0)` is
 * `"0"` — so a tape containing `-0` would not survive its own round-trip, and a
 * `subjectHash` or checkpoint taken either side of a save would disagree for a
 * reason no amount of reading the physics would explain. Adding zero is the one
 * arithmetic operation that maps `-0` to `0` and leaves every other value alone.
 */
function axis(value: number, minimum: number): number {
  return Math.round(clamp(value, minimum, 1) * AXIS_SCALE) + 0;
}

/** One run of `ticks` ticks all flown with `inputs`. */
export function encodeRun(ticks: number, inputs: FlightInputs): InputRun {
  return [
    ticks,
    axis(inputs.pitch, -1),
    axis(inputs.roll, -1),
    axis(inputs.yaw, -1),
    axis(inputs.throttle, 0),
    (inputs.firing ? FIRING_BIT : 0) |
      (inputs.clearJam ? CLEAR_JAM_BIT : 0) |
      (inputs.bombRelease ? BOMB_RELEASE_BIT : 0),
  ];
}

/**
 * The stick a run was flown with. Exactly what `quantizeInputs` produces for
 * the same stick, which is what makes a replay fly the recorded aeroplane
 * rather than one a thousandth of a deflection away from it.
 */
export function decodeRun(run: InputRun): FlightInputs {
  const [, pitch, roll, yaw, throttle, flags] = run;
  return {
    pitch: pitch / AXIS_SCALE,
    roll: roll / AXIS_SCALE,
    yaw: yaw / AXIS_SCALE,
    throttle: throttle / AXIS_SCALE,
    firing: (flags & FIRING_BIT) !== 0,
    clearJam: (flags & CLEAR_JAM_BIT) !== 0,
    bombRelease: (flags & BOMB_RELEASE_BIT) !== 0,
  };
}

/** True when two runs describe the same stick and can therefore be merged. */
function sameStick(a: InputRun, b: InputRun): boolean {
  return a[1] === b[1] && a[2] === b[2] && a[3] === b[3] && a[4] === b[4] && a[5] === b[5];
}

/**
 * Appends `ticks` ticks of `inputs` to `runs`, extending the last run when the
 * stick has not moved. `ticks <= 0` is a no-op: a rendered frame that produced
 * no whole sim tick has nothing to record, and writing a zero-length run would
 * put a hole in the tick arithmetic every seek depends on.
 */
export function appendRun(runs: InputRun[], ticks: number, inputs: FlightInputs): void {
  if (ticks <= 0) return;
  const next = encodeRun(ticks, inputs);
  const last = runs[runs.length - 1];
  if (last && sameStick(last, next)) {
    runs[runs.length - 1] = [last[0] + ticks, last[1], last[2], last[3], last[4], last[5]];
    return;
  }
  runs.push(next);
}

/** How many ticks a trace covers. */
export function traceTicks(runs: readonly InputRun[]): number {
  return runs.reduce((total, run) => total + run[0], 0);
}

/**
 * A random-access cursor over a trace.
 *
 * Seeking is the reason this is a class rather than a loop: a scrub to
 * 8 minutes has to know what the stick was at tick 28,800 without walking
 * 28,800 ticks, so the runs are indexed once by their cumulative start tick and
 * every later lookup is a binary search. `at` is called once per replayed tick,
 * so it must not be linear in the trace.
 */
export class InputTrace {
  /** `starts[i]` is the first tick run `i` covers. */
  private readonly starts: number[] = [];
  readonly ticks: number;

  constructor(private readonly runs: readonly InputRun[]) {
    let cursor = 0;
    for (const run of runs) {
      this.starts.push(cursor);
      cursor += run[0];
    }
    this.ticks = cursor;
  }

  /**
   * The stick flown on `tick`, one-based the way `SimState.tick` is: tick 1 is
   * the first tick simulated, and is covered by the first run. A tick past the
   * end of the tape returns `null` — the tape has simply run out.
   */
  at(tick: number): FlightInputs | null {
    const index = this.runIndexAt(tick - 1);
    const run = index === null ? undefined : this.runs[index];
    return run ? decodeRun(run) : null;
  }

  /** Index of the run covering zero-based `offset`, or `null` when past the end. */
  private runIndexAt(offset: number): number | null {
    if (offset < 0 || offset >= this.ticks) return null;
    let low = 0;
    let high = this.starts.length - 1;
    while (low < high) {
      const middle = Math.ceil((low + high) / 2);
      if ((this.starts[middle] ?? 0) <= offset) low = middle;
      else high = middle - 1;
    }
    return low;
  }
}
